import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Alert, Box, Button, Card, Grid, Icon, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useFormik } from "formik";
import AdicionarCliente from "layouts/clientes";
import {
  STATUS_PEDIDO,
  findStatusPedido,
} from "layouts/pedidos-venda/components/yupSchema";
import { useEffect, useState } from "react";
import ReportsService from "services/reports";
import { ptBR_DataGrid } from "utils/datagrid";
import { format } from "utils/formatDate";
import * as XLSX from "xlsx";

const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);

  return {
    startDate: startDate.toISOString().split("T")[0],
    endDate: endDate.toISOString().split("T")[0],
  };
};

const initialValues = {
  startDate: getDefaultDateRange().startDate,
  endDate: getDefaultDateRange().endDate,
  customer: null,
  status: "",
};

export default function RelatorioProdutividade() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState([]);
  const [originalReport, setOriginalReport] = useState([]);
  const [showAddCliente, setShowAddCliente] = useState(false);
  const [childData, setChildData] = useState({});
  const [totalCompras, setTotalCompras] = useState(0);
  const [totalVendas, setTotalVendas] = useState(0);
  const [totalLucro, setTotalLucro] = useState(0);
  const [quantidadePedidos, setQuantidadePedidos] = useState(0);

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async (values) => {
      setLoading(true);

      try {
        const response = await ReportsService.relatorioProdutividade({
          ...values,
          customerId: values.customer?._id || values.customer,
        });

        setOriginalReport(response.relatorio);
        setReport(response.relatorio);

        setTotalCompras(response.totais.totalCompras);
        setTotalVendas(response.totais.totalVendas);
        setTotalLucro(response.totais.totalLucro);
        setQuantidadePedidos(response.totais.quantidadePedidos);

        if (!response.relatorio.length) {
          alert("Nenhum resultado encontrado");
        }

        setLoading(false);
      } catch (error) {
        console.log("Erro ao gerar relatório:", error);
        alert("Erro ao gerar relatório");
        setLoading(false);
      }
    },
  });

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "R$ 0,00";

    const numValue = typeof value === "string" ? parseFloat(value) : value;

    if (isNaN(numValue)) return "R$ 0,00";

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numValue);
  };

  const formatDate = (date) => {
    if (!date || date === null || date === undefined) return "N/A";
    try {
      return format(new Date(date), "dd/MM/yyyy");
    } catch (error) {
      return "N/A";
    }
  };

  const getStatusText = (status) => {
    if (!status || status === null || status === undefined) return "N/A";
    return findStatusPedido(status) || status;
  };

  const exportToExcel = (data) => {
    const exportData = data.map((item) => ({
      "Número do Pedido": item.numPedido,
      "Valor Total Compra": item.valorTotalCompra,
      "Valor Total Venda": item.valorTotalVenda,
      "Data da Compra": formatDate(item.dataCompra),
      "Data da Venda": formatDate(item.dataVenda),
      Fornecedor: item.fornecedor,
      Cliente: item.cliente,
      Lucro: item.lucro,
      "Status Compra": getStatusText(item.statusCompra),
      "Status Venda": getStatusText(item.statusVenda),
      "Total Frete Vendas": item.totalFreteVendas,
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const columnWidths = [
      { wch: 15 },
      { wch: 18 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 25 },
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
    ];

    worksheet["!cols"] = columnWidths;
    XLSX.utils.book_append_sheet(workbook, worksheet, "Produtividade");
    XLSX.writeFile(
      workbook,
      `relatorio_produtividade_${new Date().getTime()}.xlsx`
    );
  };

  const selectParent = (parent, value) => {
    formik.setFieldValue(parent, value);
  };

  useEffect(() => {
    const closeOnEscapePressed = (e) => {
      if (e.key === "Escape") {
        if (showAddCliente) setShowAddCliente(false);
      }
    };
    window.addEventListener("keydown", closeOnEscapePressed);
    return () => window.removeEventListener("keydown", closeOnEscapePressed);
  }, [showAddCliente]);

  const columns = [
    {
      field: "numPedido",
      headerName: "Número do Pedido",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "valorTotalCompra",
      headerName: "Valor Total Compra",
      flex: 1,
      minWidth: 140,
      valueFormatter: (params) => {
        if (!params || params.value === null || params.value === undefined) {
          return "R$ 0,00";
        }
        return formatCurrency(params.value);
      },
    },
    {
      field: "valorTotalVenda",
      headerName: "Valor Total Venda",
      flex: 1,
      minWidth: 140,
      valueFormatter: (params) => {
        if (!params || params.value === null || params.value === undefined) {
          return "R$ 0,00";
        }
        return formatCurrency(params.value);
      },
    },
    {
      field: "dataCompra",
      headerName: "Data da Compra",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => {
        if (!params || params.value === null || params.value === undefined) {
          return "N/A";
        }
        return formatDate(params.value);
      },
    },
    {
      field: "dataVenda",
      headerName: "Data da Venda",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => {
        if (!params || params.value === null || params.value === undefined) {
          return "N/A";
        }
        return formatDate(params.value);
      },
    },
    {
      field: "fornecedor",
      headerName: "Fornecedor",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "cliente",
      headerName: "Cliente",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "lucro",
      headerName: "Lucro",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => {
        if (!params || params.value === null || params.value === undefined) {
          return "R$ 0,00";
        }
        return formatCurrency(params.value);
      },
      cellClassName: (params) => {
        if (!params || params.value === null || params.value === undefined) {
          return "";
        }
        return params.value >= 0 ? "positive-value" : "negative-value";
      },
    },
    {
      field: "statusCompra",
      headerName: "Status Compra",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => {
        if (!params || params.value === null || params.value === undefined) {
          return "N/A";
        }
        return getStatusText(params.value);
      },
    },
    {
      field: "statusVenda",
      headerName: "Status Venda",
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => {
        if (!params || params.value === null || params.value === undefined) {
          return "N/A";
        }
        return getStatusText(params.value);
      },
    },
    {
      field: "totalFreteVendas",
      headerName: "Total Frete Vendas",
      flex: 1,
      minWidth: 140,
      valueFormatter: (params) => {
        if (!params || params.value === null || params.value === undefined) {
          return "R$ 0,00";
        }
        return formatCurrency(params.value);
      },
    },
  ];

  return (
    <DashboardLayout>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <MDTypography variant="h6" color="white" mt={1}>
                  <TrendingUpIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Relatório de Produtividade
                </MDTypography>
              </MDBox>
              <MDBox pt={3} mx={2} py={3} px={2}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 2,
                    mb: 2,
                  }}
                  component="form"
                  onSubmit={formik.handleSubmit}
                >
                  <TextField
                    id="startDate"
                    name="startDate"
                    label="Data Inicial"
                    type="date"
                    value={formik.values.startDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={loading}
                    error={
                      formik.touched.startDate &&
                      Boolean(formik.errors.startDate)
                    }
                    helperText={
                      formik.touched.startDate && formik.errors.startDate
                    }
                    sx={{ width: "100%" }}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    id="endDate"
                    name="endDate"
                    label="Data Final"
                    type="date"
                    value={formik.values.endDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={loading}
                    error={
                      formik.touched.endDate && Boolean(formik.errors.endDate)
                    }
                    helperText={formik.touched.endDate && formik.errors.endDate}
                    sx={{ width: "100%" }}
                    InputLabelProps={{ shrink: true }}
                  />

                  <Alert
                    icon={false}
                    severity="success"
                    action={
                      <MDButton
                        variant="gradient"
                        color="info"
                        fullWidth
                        iconOnly
                        onClick={() => setShowAddCliente(true)}
                      >
                        <Icon fontSize="small">edit</Icon>
                      </MDButton>
                    }
                  >
                    <b>Cliente: </b>
                    {childData.customer?.razaoSocial ||
                      childData.customer?.nomeFantasia ||
                      "Todos os clientes"}
                  </Alert>

                  <TextField
                    id="status"
                    name="status"
                    label="Status da Venda"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={loading}
                    error={
                      formik.touched.status && Boolean(formik.errors.status)
                    }
                    helperText={formik.touched.status && formik.errors.status}
                    sx={{ width: "100%" }}
                    select
                    SelectProps={{ native: true }}
                  >
                    <option aria-label="Selecione" value="" />
                    <option value="">Todos os status</option>
                    {Object.values(STATUS_PEDIDO).map((opcao) => (
                      <option key={opcao} value={opcao}>
                        {findStatusPedido(opcao)}
                      </option>
                    ))}
                  </TextField>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                      gridColumn: "span 2",
                      justifySelf: "center",
                      width: "100%",
                      color: "white !important",
                    }}
                  >
                    {loading ? "Carregando..." : "Gerar Relatório"}
                  </Button>
                </Box>
              </MDBox>
            </Card>

            {report.length > 0 && (
              <>
                <Card sx={{ marginTop: 4 }}>
                  <DataGrid
                    rows={report.map((row, index) => ({ ...row, id: index }))}
                    columns={columns}
                    pageSize={100}
                    rowsPerPageOptions={[100, 200, 500]}
                    disableSelectionOnClick
                    localeText={ptBR_DataGrid}
                    sx={{
                      ".MuiDataGrid-virtualScroller": {
                        minHeight: "100px",
                      },
                      ".positive-value": {
                        color: "#2e7d32",
                        fontWeight: "bold",
                      },
                      ".negative-value": {
                        color: "#d32f2f",
                        fontWeight: "bold",
                      },
                    }}
                    disableColumnFilter
                    disableColumnMenu
                  />
                </Card>

                <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => exportToExcel(report)}
                    sx={{
                      color: "white !important",
                      backgroundColor: "#2e7d32",
                      "&:hover": {
                        backgroundColor: "#1b5e20",
                      },
                    }}
                  >
                    Exportar para Excel
                  </Button>
                </Box>
              </>
            )}

            {(totalCompras > 0 || totalVendas > 0) && (
              <Card sx={{ marginTop: 4 }}>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <MDTypography variant="h6" color="white" mt={1}>
                    Resumo Geral
                  </MDTypography>
                </MDBox>
                <MDBox pt={3} mx={2} py={3} px={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <MDTypography variant="h6" color="info">
                        Total Compras
                      </MDTypography>
                      <MDTypography variant="h4" color="info">
                        {formatCurrency(totalCompras)}
                      </MDTypography>
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <MDTypography variant="h6" color="info">
                        Total Vendas
                      </MDTypography>
                      <MDTypography variant="h4" color="info">
                        {formatCurrency(totalVendas)}
                      </MDTypography>
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <MDTypography variant="h6" color="info">
                        Lucro Total
                      </MDTypography>
                      <MDTypography
                        variant="h4"
                        color={totalLucro >= 0 ? "info" : "error"}
                      >
                        {formatCurrency(totalLucro)}
                      </MDTypography>
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <MDTypography variant="h6" color="info">
                        Quantidade de Pedidos
                      </MDTypography>
                      <MDTypography variant="h4" color="info">
                        {quantidadePedidos}
                      </MDTypography>
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
            )}
          </Grid>
        </Grid>
      </MDBox>

      {showAddCliente && (
        <MDBox
          pt={"30px"}
          pb={"5%"}
          px={"5%"}
          sx={{
            position: "absolute",
            zIndex: 9999,
            width: "100vw",
            minHeight: "100vh",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(5px)",
            top: 0,
            right: 0,
            overflow: "auto",
          }}
        >
          <AdicionarCliente
            fromParent={true}
            selectParent={(value) => {
              setChildData((prevState) =>
                Object({ ...prevState, customer: value })
              );
              selectParent("customer", value?._id);
            }}
            setShowAddCliente={setShowAddCliente}
          />
        </MDBox>
      )}
      <Footer />
    </DashboardLayout>
  );
}
